import React from 'react';

// Replaced enum with string type for dynamic configuration
export type Category = string;

export interface CriticalityLevel {
  id: string;
  label: string;
  color: string;
  icon: string;
  reviewFrequencyMonths: number;
  governancePriority: 'HIGH' | 'MEDIUM' | 'LOW';
  sortOrder: number;
}

export const DEFAULT_CRITICALITY_LEVELS: CriticalityLevel[] = [
  { id: 'Critical', label: 'Critical', color: 'red', icon: 'AlertCircle', reviewFrequencyMonths: 3, governancePriority: 'HIGH', sortOrder: 0 },
  { id: 'Supporting', label: 'Supporting', color: 'orange', icon: 'ThumbsUp', reviewFrequencyMonths: 6, governancePriority: 'MEDIUM', sortOrder: 1 },
  { id: 'Low', label: 'Low', color: 'emerald', icon: 'HelpCircle', reviewFrequencyMonths: 12, governancePriority: 'LOW', sortOrder: 2 },
];

export type PricingModel = 'PerUser' | 'Fixed' | 'Variable';

/** Taux de conversion vers la devise principale : 1 unité de cette devise = rateToMain en devise principale. */
export interface CurrencyRate {
  code: string;
  label: string;
  symbol: string;
  rateToMain: number; // 1 unit = rateToMain in main currency
}

export interface AppSettings {
  currency: string;
  currencySymbol: string;
  /** Liste des devises avec taux vers la devise principale (celle du champ currency). */
  currencyRates?: CurrencyRate[];
  renewalWarningDays: number;
  noticeWarningDays: number;
  categories: string[];
  tags: string[];
  teams: string[];
  /** Whitelisted internal company email domains used for invite warnings. */
  allowedEmailDomains?: string[];
  criticalityLevels: CriticalityLevel[];
  onboardingCompleted?: boolean;
  activationChecklist?: {
    dismissed: boolean;
    items: {
      add_first_tool: boolean;
      add_first_employee: boolean;
      create_first_training: boolean;
      configure_categories: boolean;
      create_first_team: boolean;
      assign_tool_to_employee: boolean;
      complete_first_governance_task: boolean;
    };
  };
}

export type AuthRole = 'admin' | 'super_admin' | 'user';

export interface Employee {
  id: string;
  name: string;
  firstName?: string;
  lastName?: string;
  email: string;
  role: string;
  team?: string;
  managerId?: string;
  authRole?: AuthRole;
  userId?: string;
  invitedAt?: string;
  lastLoginAt?: string;
  companyId?: string;
}

export type FeedbackType = 'GENERAL' | 'COMPLAINT' | 'FEATURE_REQUEST';
export type FeedbackStatus = 'OPEN' | 'RESOLVED';

export interface Feedback {
  id?: string; // Unique ID for management
  userId: string;
  userName: string;
  satisfaction: number; // 1-10
  proficiency: number; // 1-10
  comment: string;
  date: string;
  type?: FeedbackType;
  status?: FeedbackStatus;
}

// --- New Interfaces for Tool Management ---

export interface ProficiencyScale {
  level: number; // 1-10
  title: string; // e.g., "Novice"
  description: string; // e.g., "Can log in and view reports"
}

export interface QuizQuestion {
  id: string;
  text: string;
  type: 'multiple_choice' | 'text';
  options?: string[]; // For multiple choice
  correctAnswer?: string;
}

export interface ToolQuiz {
  id: string;
  title: string;
  description?: string;
  createdAt: string;
  questions: QuizQuestion[];
}

export interface Tool {
  id: string;
  name: string;
  category: Category;
  importance: string;
  
  // Financials
  pricingModel: PricingModel;
  basePrice: number; // Unit price for PerUser, or Total for Fixed
  userCount: number;
  monthlyCost: number; // Calculated Total (cached for easier charts)
  /** ISO code (EUR, USD, …). Si absent, = devise entreprise. */
  currency?: string;

  // Audit
  complexity: number; // 1-4 (Fixed metric based on tool nature)
  teamLiteracy: number; // 1-10 (Avg of user proficiency votes)
  
  // Governance
  renewalDate: string; // ISO Date string YYYY-MM-DD
  noticeDeadline: string; // ISO Date string YYYY-MM-DD
  leadTimeMonths?: number; // Number of months before notice deadline to start review process
  satisfactionScore: number; // 0-100 (Avg of user satisfaction votes * 10)
  
  // Relations
  teams?: string[]; // Teams that own or use the tool (multi-select)
  powerUserId?: string; // ID of the employee (Owner)
  assignedUserIds?: string[]; // IDs of employees who have access/seats
  
  // Resources
  description?: string;
  website?: string;
  notes?: string;
  trainingUrl?: string; // Link to documentation/LMS
  nextTrainingDate?: string; // ISO Date string for next live session
  tags?: string[]; // Searchable tags
  
  // Feedback History
  feedback?: Feedback[];

  // Knowledge Base (New)
  proficiencyDefinitions?: ProficiencyScale[];
  quizzes?: ToolQuiz[];

  // Vendor Contact (Phase 1.5)
  vendorContactName?: string;
  vendorContactEmail?: string;
  vendorSupportUrl?: string;
  vendorPortalUrl?: string;
  contractNumber?: string;
  accountManager?: string;
}

export interface ToolHistoryEntry {
  id: string;
  toolId: string;
  changedAt: string;
  fieldChanged: string;
  oldValue: string | null;
  newValue: string | null;
}

export interface ToolDocument {
  id: string;
  toolId: string;
  fileName: string;
  filePath: string;
  fileSize?: number;
  fileType: 'contract' | 'invoice' | 'quote' | 'other';
  notes?: string;
  uploadedAt: string;
}

export interface FeatureRequest {
  id: string;
  title: string;
  description: string;
  status: 'open' | 'planned' | 'in_progress' | 'done' | 'declined';
  adminResponse?: string;
  authorName: string;
  userId: string;
  voteCount: number;
  hasVoted?: boolean;
  createdAt: string;
}

export type Locale = 'en' | 'fr';

export interface AIRecommendation {
  type: string;
  confidence: number;
  summary: string;
  recommendations: {
    toolName: string;
    reason: string;
    estimatedCost: string;
    compatibilityScore: number;
    migrationEffort: 'low' | 'medium' | 'high';
    pros: string[];
    cons: string[];
  }[];
  savingsEstimate?: string;
  dataPoints?: string[];
}

export interface SurveyConfig {
  toolId: string;
  toolName: string;
  generatedAt: string;
  questions: {
    id: string;
    text: string;
    type: 'rating' | 'text';
  }[];
}

export interface StatMetric {
  label: string;
  value: string | number;
  change?: string;
  trend?: 'up' | 'down' | 'neutral';
  icon?: React.ReactNode;
}

export type NotificationType = 'ACCESS_REQUEST' | 'NEW_TOOL_REQUEST' | 'TRAINING_COMPLETED' | 'TRAINING_REQUEST' | 'OWNER_TASK';
export type NotificationStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'ACKNOWLEDGED' | 'FORWARDED' | 'CLARIFICATION_NEEDED' | 'DONE' | 'ABORTED';

export interface NotificationReviewData {
    adminNotes?: string;
    checklist?: {
        budgetApproved: boolean;
        securityChecked: boolean;
        techReview: boolean;
    };
    forwardedToOwnerId?: string;
}

export interface NotificationHistoryItem {
    senderId: string;
    senderName: string;
    role: 'admin' | 'user';
    message: string;
    date: string;
}

export interface Notification {
  id: string;
  type: NotificationType;
  status: NotificationStatus;
  requestKind?: 'new' | 'change';
  requesterId: string;
  requesterName: string;
  createdAt: string;
  
  // For Access Requests / Training
  toolId?: string;
  toolName?: string;
  accessReason?: string;
  accessNeededBy?: string;
  trainingNeeds?: string;

  // For New Tool Requests
  newToolData?: {
    name: string;
    purpose: string;
    alternatives: string;
    priceEstimation: string;
    termsUrl: string;
    gdprCompliant: boolean;
  };

  // Generic message (e.g. reminders)
  message?: string;
  targetUserId?: string;

  // Review Data
  reviewData?: NotificationReviewData;
  history?: NotificationHistoryItem[];
}

/** One line in the audit trail when an access/task notification is closed. */
export interface AuditLogEntry {
  id: string;
  createdAt: string;
  eventType: 'ACCESS_TASK_CLOSED' | 'GOVERNANCE_TASK_COMPLETED';
  message: string;
  notificationId?: string;
  toolName?: string;
  requesterName?: string;
  newStatus: string;
}

/** A training/formation that can be linked to one tool. One tool can have multiple curricula. */
export interface Curriculum {
  id: string;
  name: string;
  url?: string;
  toolId: string;
  createdAt?: string;
  nextDate?: string;
  description?: string;
  /** 'user' = assigned to employees; 'admin' = not assigned to employees (admin-only). */
  audience?: 'user' | 'admin';
}

export interface TrainingRecord {
  id?: string;
  employeeId: string;
  toolId: string;
  completedAt: string;
  satisfaction?: number;
  proficiency?: number;
}

/** Completion and score for one formation (curriculum). Score is on the formation, not the tool. */
export interface CurriculumCompletion {
  id: string;
  employeeId: string;
  curriculumId: string;
  completedAt: string;
  satisfaction?: number;
  proficiency?: number;
}

export interface GovernanceTask {
  id: string;
  title: string;
  date?: string;
  isCompleted: boolean;
  description?: string;
  toolId?: string;
  assigneeId?: string;
  dueDate?: string;
  status?: string;
  priority?: string;
}

export interface PlanLimits {
  max_tools: number;
  ai_queries_included: number;
  ai_overage_rate: number;
  audit_retention_days: number;
  approval_workflow_levels: number;
  has_discovery: boolean;
  has_api: boolean;
}

export interface ToolLimitResult {
  allowed: boolean;
  current: number;
  limit: number;
}

export type PlanName = 'trial' | 'tester' | 'starter' | 'pro' | 'business' | 'expired' | 'cancelled';

export interface CompanyBilling {
  id: string;
  name: string;
  plan: PlanName;
  paddleCustomerId: string | null;
  paddleSubscriptionId: string | null;
  paddleSubscriptionStatus: string;
  billingEmail: string | null;
  trialEndsAt: string | null;
  aiSpendingCap: number;
  planLimits: PlanLimits;
}

export interface PlanUsage {
  periodStart: string;
  periodEnd: string;
  toolsCount: number;
  aiQueriesUsed: number;
  aiQueriesOverage: number;
  aiOverageAmount: number;
}